import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tv, RefreshCw } from "lucide-react";

export const useTvCode = () => {
  const [code, setCode] = useState(null);
  useEffect(() => {
    api.get("/clinic/tv-code").then(({ data }) => setCode(data.code)).catch(() => {});
  }, []);
  return [code, setCode];
};

const fmt = (c) => (c ? `${c.slice(0, 3)} ${c.slice(3)}` : "··· ···");

export const TvCodeChip = () => {
  const [code] = useTvCode();
  if (!code) return null;
  return (
    <span className="hidden md:inline-flex items-center gap-1.5 border border-border px-3 h-9 text-xs text-cappuccino" data-testid="header-tv-code"
      title={`Na Smart TV, acesse ${window.location.host}/tv e digite este código`}>
      <Tv className="w-3.5 h-3.5 text-gold" /> Código TV <strong className="text-ink tracking-wider">{fmt(code)}</strong>
    </span>
  );
};

export const TvCodeCard = () => {
  const { user } = useAuth();
  const [code, setCode] = useTvCode();
  const [busy, setBusy] = useState(false);
  const regenerate = async () => {
    if (!window.confirm("Gerar um novo código? As TVs que usam o código atual precisarão digitar o novo.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/clinic/tv-code/regenerate");
      setCode(data.code);
      toast.success("Novo código gerado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-ink text-cream rounded-md p-5" data-testid="tv-code-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold flex items-center gap-2"><Tv className="w-4 h-4 text-gold" /> Smart TV (Samsung, LG e outras)</p>
          <p className="text-sm text-cream/70 mt-1">
            No navegador da TV, acesse <strong className="text-gold">{window.location.host}/tv</strong> e digite o código com o controle remoto. Não precisa de TV Box.
          </p>
        </div>
        <div className="sm:text-right shrink-0 flex flex-col sm:items-end">
          <p className="font-heading text-4xl font-extrabold tracking-[0.2em] text-gold" data-testid="tv-code-value">{fmt(code)}</p>
          {user?.role === "owner" && (
            <Button variant="ghost" size="sm" onClick={regenerate} disabled={busy} data-testid="tv-code-regenerate"
              className="text-cream/70 hover:text-cream hover:bg-white/10 gap-1.5 mt-1 h-8 rounded-sm">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Gerar novo código
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
